import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";

/**
 * A simple ShareModal component specifically for the Memory Game
 */
export const MemoryShareModal = ({
  open,
  onClose,
  onShare,
  matchedPairs,
  totalPairs,
  moves,
  timeFormatted,
  isCorrect = true, // For memory game, this will almost always be true
  successBackgroundColor = "#91B2D1",
}) => {
  // State for showing toast notification
  const [shareToastVisible, setShareToastVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [confettiPieces, setConfettiPieces] = useState(200);

  // Control confetti animation
  useEffect(() => {
    if (open && isCorrect) {
      setShowConfetti(true);
      setConfettiPieces(200);

      // Gradually reduce confetti pieces for a fade-out effect
      const duration = 4000; // 4 seconds of full confetti
      const fadeOutTime = 1000; // 1 second to fade out
      const fadeOutInterval = 50; // Update every 50ms
      const decrementSteps = fadeOutTime / fadeOutInterval;
      const decrementAmount = Math.ceil(200 / decrementSteps);

      // Start the fade-out after the duration
      const timer = setTimeout(() => {
        const fadeInterval = setInterval(() => {
          setConfettiPieces((prev) => {
            const newValue = prev - decrementAmount;
            if (newValue <= 0) {
              clearInterval(fadeInterval);
              setShowConfetti(false);
              return 0;
            }
            return newValue;
          });
        }, fadeOutInterval);
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [open, isCorrect]);

  // Function to show toast and hide it after a delay
  const showShareToast = () => {
    setShareToastVisible(true);
    setTimeout(() => {
      setShareToastVisible(false);
    }, 3000);
  };

  // Handle share button click
  const handleShareClick = () => {
    onShare();
    showShareToast();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "400px",
          bgcolor: isCorrect ? successBackgroundColor : "white",
          borderRadius: "8px",
          p: 4,
          pb: 7,
          outline: "none",
          textAlign: "center",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
          overflow: "hidden", // Added to contain the toast notification
        }}
      >
        {/* Confetti animation for success */}
        {showConfetti && isCorrect && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={true}
            numberOfPieces={confettiPieces}
            gravity={0.15}
            colors={[
              "#FFD700", // Gold
              "#3D59A3", // Afro News Blue
              "#FF4500", // Red-Orange
              "#FF6347", // Tomato
              "#9370DB", // Medium Purple
            ]}
            opacity={confettiPieces < 100 ? confettiPieces / 100 : 1}
          />
        )}

        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: isCorrect ? "white" : "black",
          }}
        >
          <CloseIcon />
        </IconButton>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontWeight: "bold",
              color: isCorrect ? "white" : "black",
            }}
          >
            {isCorrect ? "Well Done!" : "Better luck next time!"}
          </Typography>
        </motion.div>

        <Box
          sx={{
            mb: 4,
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Game Summary
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              Moves:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {moves}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              Time:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {timeFormatted}
            </Typography>
          </Box>
        </Box>

        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleShareClick}
            sx={{
              bgcolor: isCorrect ? "white" : "#4caf50",
              color: isCorrect ? successBackgroundColor : "white",
              fontWeight: "bold",
              borderRadius: "8px",
              py: 1.5,
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              "&:hover": {
                bgcolor: isCorrect ? "#f5f5f5" : "#43a047",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
              },
            }}
          >
            Share Results
          </Button>
        </motion.div>

        {/* Toast notification for successful copy */}
        <AnimatePresence>
          {shareToastVisible && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                width: "100%",
                zIndex: 1010,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#43a047",
                  color: "white",
                  fontWeight: "bold",
                  padding: "12px",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.15)",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "50%",
                      width: 22,
                      height: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "15px",
                        color: "#43a047",
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </Box>
                  </Box>
                  Results copied to clipboard!
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Modal>
  );
};

export default MemoryShareModal;
